import { useEffect, useId, useRef, type ReactNode } from 'react';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { MONTHLY_CREDITS } from '@/billing/buildCostNote';
import { offerButton, offerTerms, type Offer } from '../../state/trial';
import { Button } from '../../ui';

/** At most this many of the plan's features: enough to recognise the app, short enough for a phone. */
const FEATURES_SHOWN = 5;

/*
 * What comes with the plan, in the words of what the person will do with it. Each is a thing the
 * product does today for every app it builds: a published page at its own address
 * (app.saddle-sites.com/site/<name>), accounts and saved rows for the people who use it (the
 * runtime's S.auth and S.list), changes made through the editor's chat, and the monthly grant
 * (the server's PAID_GRANT_CREDITS, which a trial gets too).
 */
const WHAT_YOU_GET = [
  'Your site, live at its own link',
  'Sign-ins and saved data for the people who use it',
  'Changes made just by chatting',
  `${MONTHLY_CREDITS.toLocaleString('en-US')} credits a month for builds and edits`,
];

/**
 * The moment the card is asked for, for somebody without a plan.
 *
 * It was one line of terms over a button, and then Stripe: the person had walked the plan, but
 * the screen that asked for their card said nothing about what the card was for. This says it —
 * their app by name and what it will do, what comes with the plan, the price in the shared words
 * (state/trial.ts, so it cannot drift from Credits or the return from Stripe) — and one tap goes
 * on. A subscriber never sees it: Build builds.
 */
export default function Paywall({
  plan,
  offer,
  busy,
  continueLabel,
  notice,
  ready = false,
  building,
  onContinue,
  onBack,
}: {
  plan: AppPlan;
  offer: Offer;
  busy: boolean;
  /** The button's words: the offer's, or "Continue to checkout" once a warning has been said. */
  continueLabel?: string;
  /** Said above the button: why the last press stopped, or what checkout cannot carry. */
  notice?: ReactNode;
  /**
   * The app is built and waiting on the server for the trial (state/buildRun.ts `heldApp`): said,
   * so the card finishes something that already exists. Its page is not shown until it is paid for.
   */
  ready?: boolean;
  /**
   * The app is being built behind this panel (2026-10-05), and where it has got to. Said plainly:
   * how long it takes, and that the trial can start now and opens it when it is done.
   */
  building?: string;
  onContinue: () => void;
  onBack: () => void;
}) {
  const headingId = useId();
  const doesId = useId();
  const getsId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const terms = offerTerms(offer);
  const features = plan.features.filter((f) => f.included).slice(0, FEATURES_SHOWN);
  const label = continueLabel ?? offerButton(offer);

  // The press that opened this was at the bottom of a long step; its top is what they need to see.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <section className="paywall" aria-labelledby={headingId}>
      <h2 id={headingId} ref={heading} tabIndex={-1} className="type-title paywall-name">{plan.appName}</h2>
      {ready ? (
        <p className="type-body paywall-ready" role="status">Your {plan.appName} is ready to go live.</p>
      ) : building ? (
        <div className="paywall-building" role="status">
          <p className="type-body paywall-ready">
            Your {plan.appName} is building. Start your 3 free days to open it the moment it’s ready.
          </p>
          <p className="type-caption paywall-building-step">
            <span className="paywall-building-dot" aria-hidden="true" /> {building} It takes about 4 minutes.
          </p>
        </div>
      ) : (
        plan.tagline && <p className="type-body plan-detail">{plan.tagline}</p>
      )}

      {features.length > 0 && (
        <>
          <h3 id={doesId} className="type-micro section-label">What it does</h3>
          <ul className="plan-review-list" aria-labelledby={doesId}>
            {features.map((f) => (
              <li key={f.id} className="type-body">{f.label}</li>
            ))}
          </ul>
        </>
      )}

      <h3 id={getsId} className="type-micro section-label">What you get</h3>
      <ul className="paywall-gets" aria-labelledby={getsId}>
        {WHAT_YOU_GET.map((line) => (
          <li key={line} className="type-body">
            <span className="paywall-tick" aria-hidden="true">✓</span>
            {line}
          </li>
        ))}
      </ul>

      <div className="cost-note tone-invite" role="note">
        <span className="type-body cost-headline">{terms.headline}</span>{' '}
        <span className="type-caption cost-detail">{terms.detail}</span>
      </div>

      {notice}

      <div className="paywall-actions">
        <Button title={label} onPress={onContinue} loading={busy} accessibilityLabel={`${label} and build this app`} />
        <Button title="Back to your plan" variant="ghost" onPress={onBack} disabled={busy} />
      </div>
    </section>
  );
}
